import React, { useEffect, useState, useRef } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { 
  FileText, Plus, Edit, Trash2, Settings, Eye, Download, Mail, 
  Users, ChevronDown, X, Printer, Send, CheckCircle, AlertCircle,
  Calendar, TrendingUp, FileSpreadsheet
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminInvoicesPage = () => {
  const { getAuthHeaders } = useApp();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pricing, setPricing] = useState({ currency: 'GBP' });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('invoices'); // 'invoices' or 'revenue'
  
  // Email status tracking
  const [emailStatus, setEmailStatus] = useState({}); // { invoiceId: 'sent' | 'error' | 'sending' }
  
  // Revenue filters
  const [revenueFilter, setRevenueFilter] = useState('month'); // 'month', 'year', 'custom'
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  
  // Dialogs
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showEditInvoiceDialog, setShowEditInvoiceDialog] = useState(false);
  
  // Forms
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({
    name: '', address_line1: '', address_line2: '', city: '', postal_code: '', country: 'United Kingdom', email: '', phone: ''
  });
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);

  const printRef = useRef();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    await Promise.all([fetchInvoices(), fetchClients(), fetchSettings(), fetchPricing()]);
  };

  const fetchInvoices = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/invoices`, { headers: getAuthHeaders() });
      setInvoices(data);
    } catch (error) {
      console.error('Failed to fetch invoices');
    }
  };

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/invoice/clients`, { headers: getAuthHeaders() });
      setClients(data);
    } catch (error) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/invoice/settings`, { headers: getAuthHeaders() });
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const fetchPricing = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/work/pricing`, { headers: getAuthHeaders() });
      setPricing(data);
    } catch (error) {
      console.error('Failed to fetch pricing');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/invoice/settings`, settings, { headers: getAuthHeaders() });
      toast.success('Settings saved!');
      setShowSettingsDialog(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleSaveClient = async () => {
    try {
      if (editingClient) {
        await axios.put(`${BACKEND_URL}/api/invoice/clients/${editingClient}`, clientForm, { headers: getAuthHeaders() });
        toast.success('Client updated!');
      } else {
        await axios.post(`${BACKEND_URL}/api/invoice/clients`, clientForm, { headers: getAuthHeaders() });
        toast.success('Client added!');
      }
      setShowClientDialog(false);
      resetClientForm();
      fetchClients();
    } catch (error) {
      toast.error('Failed to save client');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/invoice/clients/${clientId}`, { headers: getAuthHeaders() });
      toast.success('Client deleted');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client.id);
    setClientForm(client);
    setShowClientDialog(true);
  };

  const resetClientForm = () => {
    setEditingClient(null);
    setClientForm({
      name: '', address_line1: '', address_line2: '', city: '', postal_code: '', country: 'United Kingdom', email: '', phone: ''
    });
  };

  const handleGenerateInvoice = async () => {
    if (!selectedWeekStart || !selectedClientId) {
      toast.error('Please select a week and client');
      return;
    }
    
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('week_start', selectedWeekStart);
      formData.append('client_id', selectedClientId);
      
      const { data } = await axios.post(`${BACKEND_URL}/api/invoices/generate-from-work`, formData, {
        headers: getAuthHeaders()
      });
      
      toast.success('Invoice generated!');
      setShowGenerateDialog(false);
      setPreviewInvoice(data);
      setShowPreviewDialog(true);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/invoices/${invoiceId}`, { headers: getAuthHeaders() });
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handlePreview = (invoice) => {
    setPreviewInvoice(invoice);
    setShowPreviewDialog(true);
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice({ ...invoice });
    setShowEditInvoiceDialog(true);
  };

  const handleSaveInvoice = async () => {
    if (!editingInvoice) return;
    try {
      await axios.put(`${BACKEND_URL}/api/invoices/${editingInvoice.id}`, editingInvoice, {
        headers: getAuthHeaders()
      });
      toast.success('Invoice updated!');
      setShowEditInvoiceDialog(false);
      setEditingInvoice(null);
      fetchInvoices();
      // Update preview if it's the same invoice
      if (previewInvoice && previewInvoice.id === editingInvoice.id) {
        setPreviewInvoice(editingInvoice);
      }
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  // Get client email by client_id
  const getClientEmail = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.email || '';
  };

  // Send Email
  const handleSendEmail = async (invoice) => {
    const clientEmail = getClientEmail(invoice.client_id);
    if (!clientEmail) {
      toast.error('Client does not have an email address');
      return;
    }
    
    // Set sending status
    setEmailStatus(prev => ({ ...prev, [invoice.id]: 'sending' }));
    
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('to_email', clientEmail);
      
      const { data } = await axios.post(`${BACKEND_URL}/api/invoices/${invoice.id}/send-email`, formData, { 
        headers: getAuthHeaders() 
      });
      
      toast.success(`Invoice sent to ${clientEmail}`);
      setEmailStatus(prev => ({ ...prev, [invoice.id]: 'sent' }));
      
      // Update invoice status to 'sent'
      await axios.put(`${BACKEND_URL}/api/invoices/${invoice.id}`, {
        ...invoice,
        status: 'sent'
      }, { headers: getAuthHeaders() });
      
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email. Check email settings.');
      setEmailStatus(prev => ({ ...prev, [invoice.id]: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  // Revenue calculations
  const getFilteredInvoices = () => {
    const now = new Date();
    let startDate, endDate;
    
    if (revenueFilter === 'month') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (revenueFilter === 'year') {
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = new Date(now.getFullYear(), 11, 31);
    } else if (revenueFilter === 'custom' && customDateStart && customDateEnd) {
      startDate = new Date(customDateStart);
      endDate = new Date(customDateEnd);
    } else {
      return invoices;
    }
    
    return invoices.filter(inv => {
      const invDate = new Date(inv.invoice_date);
      return invDate >= startDate && invDate <= endDate;
    });
  };

  const getRevenueStats = () => {
    const filtered = getFilteredInvoices();
    const total = filtered.reduce((sum, inv) => sum + (inv.total || 0), 0);
    const paid = filtered.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.total || 0), 0);
    const pending = filtered.filter(inv => inv.status !== 'paid').reduce((sum, inv) => sum + (inv.total || 0), 0);
    return { total, paid, pending, count: filtered.length };
  };

  const exportToCSV = () => {
    const filtered = getFilteredInvoices();
    const headers = ['Invoice Number', 'Client', 'Date', 'Due Date', 'Amount', 'Status'];
    const rows = filtered.map(inv => [
      inv.invoice_number,
      inv.client_name,
      inv.invoice_date,
      inv.due_date,
      inv.total?.toFixed(2),
      inv.status
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `revenue_${revenueFilter}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const generateInvoiceHTML = (invoice) => {
    if (!invoice || !settings) return '';
    
    const currencySymbol = pricing.currency === 'EUR' ? '€' : '£';
    
    // Filter client address to remove duplicate name
    const addressLines = (invoice.client_address || '').split('\n').filter((line, i) => 
      !(i === 0 && line.trim() === invoice.client_name?.trim()) && line.trim()
    );
    
    const itemsHTML = (invoice.items || []).map((item, index) => `
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #ddd; font-size: 12px;">${index + 1}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd; font-size: 12px; white-space: pre-line;">${item.description || ''}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right; font-size: 12px;">${(item.quantity || 1).toFixed(2)}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right; font-size: 12px;">${(item.rate || 0).toFixed(2)}</td>
        <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right; font-size: 12px;">${(item.amount || 0).toFixed(2)}</td>
      </tr>
    `).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          @page { 
            size: A4; 
            margin: 15mm; 
            /* Remove browser headers and footers */
            margin-top: 0;
            margin-bottom: 0;
          }
          @media print {
            html, body {
              margin: 0 !important;
              padding: 15mm !important;
            }
            /* Hide URL, date, page numbers */
            @page { margin: 15mm; }
          }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { 
            font-family: Arial, Helvetica, sans-serif; 
            font-size: 12px; 
            color: #333; 
            line-height: 1.4;
            padding: 20px;
            max-width: 210mm;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header { display: flex; justify-content: space-between; margin-bottom: 25px; }
          .my-info { font-size: 11px; }
          .my-info p { margin: 2px 0; }
          .my-info .name { font-weight: bold; font-size: 14px; margin-bottom: 5px; }
          .invoice-title { text-align: right; }
          .invoice-title h1 { font-size: 32px; color: #333; margin: 0; }
          .invoice-title .number { color: #666; font-size: 12px; margin-top: 5px; }
          .balance-box { margin-top: 15px; text-align: right; }
          .balance-label { font-size: 10px; color: #666; text-transform: uppercase; }
          .balance-amount { font-size: 20px; font-weight: bold; }
          .details-row { display: flex; justify-content: space-between; margin: 20px 0; }
          .bill-to { max-width: 250px; }
          .bill-to-label { font-size: 10px; color: #666; text-transform: uppercase; margin-bottom: 5px; }
          .bill-to .client-name { font-weight: bold; font-size: 13px; margin-bottom: 3px; }
          .bill-to p { font-size: 11px; margin: 2px 0; color: #555; }
          .invoice-details { text-align: right; font-size: 11px; }
          .invoice-details p { margin: 3px 0; }
          .invoice-details span { color: #666; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th { 
            background: #4a5568; 
            color: white; 
            padding: 8px; 
            text-align: left; 
            font-size: 10px; 
            text-transform: uppercase;
          }
          th:nth-child(3), th:nth-child(4), th:nth-child(5) { text-align: right; }
          td:nth-child(3), td:nth-child(4), td:nth-child(5) { text-align: right; }
          .totals { text-align: right; margin-top: 10px; }
          .totals-row { display: flex; justify-content: flex-end; margin: 5px 0; }
          .totals-label { width: 100px; text-align: right; padding-right: 15px; }
          .totals-value { width: 80px; text-align: right; }
          .total-final { font-weight: bold; font-size: 14px; }
          .balance-due-box { 
            background: #4a5568; 
            color: white; 
            padding: 8px 15px; 
            display: inline-block; 
            margin-top: 10px;
            font-size: 12px;
          }
          .notes-section { margin-top: 25px; font-size: 11px; }
          .notes-section h4 { font-size: 12px; margin-bottom: 5px; }
          .notes-section p { color: #555; line-height: 1.5; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="my-info">
            <p class="name">${settings.my_name || ''}</p>
            <p>${settings.my_address_line1 || ''}</p>
            <p>${settings.my_address_line2 || ''}</p>
            <p>${settings.my_country || ''}</p>
          </div>
          <div class="invoice-title">
            <h1>INVOICE</h1>
            <p class="number"># ${invoice.invoice_number}</p>
            <div class="balance-box">
              <p class="balance-label">Balance Due</p>
              <p class="balance-amount">${currencySymbol}${(invoice.total || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div class="details-row">
          <div class="bill-to">
            <p class="bill-to-label">Bill To</p>
            <p class="client-name">${invoice.client_name || ''}</p>
            ${addressLines.map(line => `<p>${line}</p>`).join('')}
          </div>
          <div class="invoice-details">
            <p><span>Invoice Date:</span> ${invoice.invoice_date || ''}</p>
            <p><span>Terms:</span> ${invoice.terms || ''}</p>
            <p><span>Due Date:</span> ${invoice.due_date || ''}</p>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 30px;">#</th>
              <th>Item & Description</th>
              <th style="width: 60px;">Qty</th>
              <th style="width: 70px;">Rate</th>
              <th style="width: 80px;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>

        <div class="totals">
          <div class="totals-row">
            <span class="totals-label">Sub Total</span>
            <span class="totals-value">${(invoice.subtotal || invoice.total || 0).toFixed(2)}</span>
          </div>
          <div class="totals-row total-final">
            <span class="totals-label">Total</span>
            <span class="totals-value">${currencySymbol}${(invoice.total || 0).toFixed(2)}</span>
          </div>
          <div class="balance-due-box">
            <strong>Balance Due</strong> &nbsp;&nbsp; ${currencySymbol}${(invoice.total || 0).toFixed(2)}
          </div>
        </div>

        ${invoice.notes ? `
        <div class="notes-section">
          <h4>Notes</h4>
          <p>${invoice.notes}</p>
        </div>
        ` : ''}

        ${invoice.payment_terms ? `
        <div class="notes-section">
          <h4>Terms & Conditions</h4>
          <p>${invoice.payment_terms}</p>
        </div>
        ` : ''}
      </body>
      </html>
    `;
  };

  const handlePrint = () => {
    if (!previewInvoice) return;
    
    const html = generateInvoiceHTML(previewInvoice);
    const WinPrint = window.open('', '', 'width=800,height=600');
    WinPrint.document.write(html);
    WinPrint.document.close();
    WinPrint.focus();
    
    // Show instruction toast
    toast.info('În setările de print, debifează "Headers and footers" pentru a elimina data/ora', {
      duration: 5000
    });
    
    setTimeout(() => {
      WinPrint.print();
      WinPrint.close();
    }, 250);
  };

  const handleDownloadPDF = (invoice) => {
    // Generate and open print dialog for the invoice
    const html = generateInvoiceHTML(invoice);
    const WinPrint = window.open('', '', 'width=800,height=600');
    WinPrint.document.write(html);
    WinPrint.document.close();
    WinPrint.focus();
    
    // Show instruction toast
    toast.info('În setările de print: More settings → debifează "Headers and footers"', {
      duration: 5000
    });
    
    setTimeout(() => {
      WinPrint.print();
      WinPrint.close();
    }, 250);
  };

  const formatCurrency = (amount) => {
    const symbol = pricing.currency === 'EUR' ? '€' : '£';
    return `${symbol}${(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Get available weeks for selection (last 8 weeks)
  const getAvailableWeeks = () => {
    const weeks = [];
    const today = new Date();
    for (let i = 0; i < 8; i++) {
      const weekStart = new Date(today);
      const dayOfWeek = weekStart.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      weekStart.setDate(weekStart.getDate() - diff - (i * 7));
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weeks.push({
        value: weekStart.toISOString().split('T')[0],
        label: `${weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
      });
    }
    return weeks;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-serif">Invoices</h1>
            <p className="text-muted-foreground">Manage invoices and clients</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => setShowSettingsDialog(true)} variant="outline">
              <Settings size={18} className="mr-2" />
              Settings
            </Button>
            <Button onClick={() => { resetClientForm(); setShowClientDialog(true); }} variant="outline">
              <Users size={18} className="mr-2" />
              Clients
            </Button>
            <Button onClick={() => setShowGenerateDialog(true)} className="bg-primary">
              <Plus size={18} className="mr-2" />
              Generate Invoice
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-white/10">
          <button
            onClick={() => setActiveTab('invoices')}
            className={`pb-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'invoices' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-white'
            }`}
          >
            <FileText size={16} className="inline mr-2" />
            Invoices
          </button>
          <button
            onClick={() => setActiveTab('revenue')}
            className={`pb-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'revenue' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-white'
            }`}
          >
            <TrendingUp size={16} className="inline mr-2" />
            Revenue
          </button>
        </div>

        {activeTab === 'invoices' && (
          <>
            {/* Clients List */}
            <div className="glass p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Clients</h3>
                <Button onClick={() => { resetClientForm(); setShowClientDialog(true); }} size="sm">
                  <Plus size={16} className="mr-1" /> Add Client
                </Button>
              </div>
              {clients.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No clients yet. Add your first client.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {clients.map(client => (
                    <div key={client.id} className="bg-slate-800/50 p-4 rounded-lg">
                      <h4 className="font-semibold">{client.name}</h4>
                      <p className="text-sm text-muted-foreground">{client.address_line1}</p>
                      <p className="text-sm text-muted-foreground">{client.city} {client.postal_code}</p>
                      <p className="text-sm text-muted-foreground">{client.email}</p>
                      <div className="flex gap-2 mt-3">
                        <Button onClick={() => handleEditClient(client)} size="sm" variant="outline">
                          <Edit size={14} />
                        </Button>
                        <Button onClick={() => handleDeleteClient(client.id)} size="sm" variant="destructive">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Invoices List */}
            <div className="glass p-6">
              <h3 className="text-lg font-semibold mb-4">All Invoices</h3>
              {invoices.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No invoices yet. Generate your first invoice.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Invoice #</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Client</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Date</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Due Date</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Amount</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Status</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(invoice => (
                    <tr key={invoice.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-3 font-mono">{invoice.invoice_number}</td>
                      <td className="p-3">{invoice.client_name}</td>
                      <td className="p-3 text-muted-foreground">{formatDate(invoice.invoice_date)}</td>
                      <td className="p-3 text-muted-foreground">{formatDate(invoice.due_date)}</td>
                      <td className="p-3 font-semibold text-green-400">{formatCurrency(invoice.total)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs uppercase rounded ${
                          invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                          invoice.status === 'sent' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1 flex-wrap">
                          <Button onClick={() => handlePreview(invoice)} size="sm" variant="outline" title="Preview">
                            <Eye size={14} />
                          </Button>
                          <Button onClick={() => handleEditInvoice(invoice)} size="sm" variant="outline" title="Edit">
                            <Edit size={14} />
                          </Button>
                          <Button onClick={() => handleDownloadPDF(invoice)} size="sm" variant="outline" title="Download PDF">
                            <Download size={14} />
                          </Button>
                          {getClientEmail(invoice.client_id) && (
                            <>
                              <Button 
                                onClick={() => handleSendEmail(invoice)} 
                                size="sm" 
                                variant="outline" 
                                title={`Send to ${getClientEmail(invoice.client_id)}`}
                                disabled={loading || emailStatus[invoice.id] === 'sending'}
                                className="text-blue-400 border-blue-400/30 hover:bg-blue-400/10"
                              >
                                {emailStatus[invoice.id] === 'sending' ? (
                                  <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Send size={14} />
                                )}
                              </Button>
                              {/* Email status indicator */}
                              {emailStatus[invoice.id] === 'sent' && (
                                <span className="flex items-center text-green-400" title="Email sent successfully">
                                  <CheckCircle size={16} />
                                </span>
                              )}
                              {emailStatus[invoice.id] === 'error' && (
                                <span className="flex items-center text-red-400" title="Email failed to send">
                                  <AlertCircle size={16} />
                                </span>
                              )}
                            </>
                          )}
                          <Button onClick={() => handleDeleteInvoice(invoice.id)} size="sm" variant="destructive" title="Delete">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-6">
            {/* Revenue Filter */}
            <div className="glass p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Calendar size={20} className="text-primary" />
                  Select Period
                </h3>
                <Button onClick={exportToCSV} variant="outline" className="border-green-500/30 text-green-400">
                  <FileSpreadsheet size={16} className="mr-2" />
                  Export CSV
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex gap-2">
                  <button
                    onClick={() => setRevenueFilter('month')}
                    className={`px-4 py-2 rounded transition-all ${
                      revenueFilter === 'month' 
                        ? 'bg-primary text-white' 
                        : 'bg-slate-800 text-muted-foreground hover:bg-slate-700'
                    }`}
                  >
                    This Month
                  </button>
                  <button
                    onClick={() => setRevenueFilter('year')}
                    className={`px-4 py-2 rounded transition-all ${
                      revenueFilter === 'year' 
                        ? 'bg-primary text-white' 
                        : 'bg-slate-800 text-muted-foreground hover:bg-slate-700'
                    }`}
                  >
                    This Year
                  </button>
                  <button
                    onClick={() => setRevenueFilter('custom')}
                    className={`px-4 py-2 rounded transition-all ${
                      revenueFilter === 'custom' 
                        ? 'bg-primary text-white' 
                        : 'bg-slate-800 text-muted-foreground hover:bg-slate-700'
                    }`}
                  >
                    Custom
                  </button>
                </div>
                
                {revenueFilter === 'custom' && (
                  <div className="flex gap-2 items-center">
                    <input
                      type="date"
                      value={customDateStart}
                      onChange={(e) => setCustomDateStart(e.target.value)}
                      className="bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                    />
                    <span className="text-muted-foreground">to</span>
                    <input
                      type="date"
                      value={customDateEnd}
                      onChange={(e) => setCustomDateEnd(e.target.value)}
                      className="bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Revenue Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="glass p-6 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Invoices</p>
                <p className="text-3xl font-bold">{getRevenueStats().count}</p>
              </div>
              <div className="glass p-6 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Revenue</p>
                <p className="text-3xl font-bold text-primary">{formatCurrency(getRevenueStats().total)}</p>
              </div>
              <div className="glass p-6 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Paid</p>
                <p className="text-3xl font-bold text-green-400">{formatCurrency(getRevenueStats().paid)}</p>
              </div>
              <div className="glass p-6 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Pending</p>
                <p className="text-3xl font-bold text-yellow-400">{formatCurrency(getRevenueStats().pending)}</p>
              </div>
            </div>

            {/* Filtered Invoices Table */}
            <div className="glass p-6">
              <h3 className="text-lg font-semibold mb-4">
                Invoices for {revenueFilter === 'month' ? 'This Month' : revenueFilter === 'year' ? 'This Year' : 'Selected Period'}
              </h3>
              {getFilteredInvoices().length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No invoices for this period.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Invoice #</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Client</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Date</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Amount</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider text-primary">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredInvoices().map(invoice => (
                        <tr key={invoice.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="p-3 font-mono">{invoice.invoice_number}</td>
                          <td className="p-3">{invoice.client_name}</td>
                          <td className="p-3 text-muted-foreground">{formatDate(invoice.invoice_date)}</td>
                          <td className="p-3 font-semibold text-green-400">{formatCurrency(invoice.total)}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 text-xs uppercase rounded ${
                              invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                              invoice.status === 'sent' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {invoice.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-white/20">
                        <td colSpan="3" className="p-3 font-semibold text-right">Total:</td>
                        <td className="p-3 font-bold text-xl text-primary">{formatCurrency(getRevenueStats().total)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Dialog */}
        <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
          <DialogContent className="bg-slate-950 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Invoice Settings</DialogTitle>
            </DialogHeader>
            {settings && (
              <div className="space-y-4 mt-4">
                <h4 className="text-sm font-semibold text-primary uppercase tracking-wider">My Details</h4>
                <input
                  type="text"
                  placeholder="Name / Company"
                  value={settings.my_name}
                  onChange={(e) => setSettings({ ...settings, my_name: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Address Line 1"
                  value={settings.my_address_line1}
                  onChange={(e) => setSettings({ ...settings, my_address_line1: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Address Line 2"
                  value={settings.my_address_line2}
                  onChange={(e) => setSettings({ ...settings, my_address_line2: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Country"
                  value={settings.my_country}
                  onChange={(e) => setSettings({ ...settings, my_country: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                />
                
                <h4 className="text-sm font-semibold text-primary uppercase tracking-wider mt-6">Bank Details</h4>
                <input
                  type="text"
                  placeholder="Bank Name"
                  value={settings.bank_name}
                  onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Sort Code"
                    value={settings.sort_code}
                    onChange={(e) => setSettings({ ...settings, sort_code: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Account Number"
                    value={settings.account_number}
                    onChange={(e) => setSettings({ ...settings, account_number: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                  />
                </div>

                <h4 className="text-sm font-semibold text-primary uppercase tracking-wider mt-6">Invoice Settings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Invoice Prefix (e.g., INV-)"
                    value={settings.invoice_prefix}
                    onChange={(e) => setSettings({ ...settings, invoice_prefix: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                  />
                  <input
                    type="number"
                    placeholder="Next Invoice Number"
                    value={settings.next_invoice_number}
                    onChange={(e) => setSettings({ ...settings, next_invoice_number: parseInt(e.target.value) })}
                    className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Default Terms"
                  value={settings.default_terms}
                  onChange={(e) => setSettings({ ...settings, default_terms: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                />
                <textarea
                  placeholder="Default Notes"
                  value={settings.default_notes}
                  onChange={(e) => setSettings({ ...settings, default_notes: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 h-20"
                />
                <textarea
                  placeholder="Payment Terms Text"
                  value={settings.payment_terms}
                  onChange={(e) => setSettings({ ...settings, payment_terms: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 h-20"
                />

                <Button onClick={handleSaveSettings} className="w-full bg-primary">
                  Save Settings
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Client Dialog */}
        <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
          <DialogContent className="bg-slate-950 border-white/10">
            <DialogHeader>
              <DialogTitle>{editingClient ? 'Edit Client' : 'Add Client'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <input
                type="text"
                placeholder="Company/Client Name"
                value={clientForm.name}
                onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
              />
              <input
                type="text"
                placeholder="Address Line 1"
                value={clientForm.address_line1}
                onChange={(e) => setClientForm({ ...clientForm, address_line1: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
              />
              <input
                type="text"
                placeholder="Address Line 2"
                value={clientForm.address_line2}
                onChange={(e) => setClientForm({ ...clientForm, address_line2: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="City"
                  value={clientForm.city}
                  onChange={(e) => setClientForm({ ...clientForm, city: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                />
                <input
                  type="text"
                  placeholder="Postal Code"
                  value={clientForm.postal_code}
                  onChange={(e) => setClientForm({ ...clientForm, postal_code: e.target.value })}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                />
              </div>
              <input
                type="text"
                placeholder="Country"
                value={clientForm.country}
                onChange={(e) => setClientForm({ ...clientForm, country: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
              />
              <input
                type="email"
                placeholder="Email"
                value={clientForm.email}
                onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
              />
              <input
                type="tel"
                placeholder="Phone"
                value={clientForm.phone}
                onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
              />
              <Button onClick={handleSaveClient} className="w-full bg-primary">
                {editingClient ? 'Update Client' : 'Add Client'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Generate Invoice Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className="bg-slate-950 border-white/10">
            <DialogHeader>
              <DialogTitle>Generate Invoice from Work Hours</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Select Week</label>
                <select
                  value={selectedWeekStart}
                  onChange={(e) => setSelectedWeekStart(e.target.value)}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                >
                  <option value="">-- Select Week --</option>
                  {getAvailableWeeks().map(week => (
                    <option key={week.value} value={week.value}>{week.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Select Client</label>
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                >
                  <option value="">-- Select Client --</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <Button onClick={handleGenerateInvoice} disabled={loading} className="w-full bg-primary">
                {loading ? 'Generating...' : 'Generate Invoice'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invoice Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="bg-white text-black max-w-4xl max-h-[95vh] overflow-y-auto">
            <DialogHeader className="flex flex-row items-center justify-between">
              <DialogTitle className="text-black">Invoice Preview</DialogTitle>
              <div className="flex gap-2">
                <Button onClick={handlePrint} size="sm" className="bg-primary text-white">
                  <Printer size={16} className="mr-1" /> Print / PDF
                </Button>
              </div>
            </DialogHeader>
            
            {previewInvoice && settings && (
              <div id="invoice-print-area" className="p-8 bg-white">
                {/* Invoice Header */}
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <p className="font-bold text-lg">{settings.my_name}</p>
                    <p className="text-gray-600">{settings.my_address_line1}</p>
                    <p className="text-gray-600">{settings.my_address_line2}</p>
                    <p className="text-gray-600">{settings.my_country}</p>
                  </div>
                  <div className="text-right">
                    <h1 className="text-4xl font-bold">INVOICE</h1>
                    <p className="text-gray-600"># {previewInvoice.invoice_number}</p>
                    <div className="mt-4">
                      <p className="text-sm text-gray-500">Balance Due</p>
                      <p className="text-2xl font-bold">{formatCurrency(previewInvoice.total)}</p>
                    </div>
                  </div>
                </div>

                {/* Bill To & Invoice Details */}
                <div className="flex justify-between mb-8">
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Bill To</p>
                    <p className="font-bold">{previewInvoice.client_name}</p>
                    {previewInvoice.client_address.split('\n').filter((line, i) => 
                      // Skip first line if it matches client name (avoid duplicate)
                      !(i === 0 && line.trim() === previewInvoice.client_name?.trim())
                    ).map((line, i) => (
                      line.trim() && <p key={i} className="text-gray-600">{line}</p>
                    ))}
                  </div>
                  <div className="text-right">
                    <p><span className="text-gray-500">Invoice Date:</span> {formatDate(previewInvoice.invoice_date)}</p>
                    <p><span className="text-gray-500">Terms:</span> {previewInvoice.terms}</p>
                    <p><span className="text-gray-500">Due Date:</span> {formatDate(previewInvoice.due_date)}</p>
                  </div>
                </div>

                {/* Items Table */}
                <table className="w-full mb-8">
                  <thead>
                    <tr className="bg-gray-700 text-white">
                      <th className="text-left p-3 text-sm">#</th>
                      <th className="text-left p-3 text-sm">Item & Description</th>
                      <th className="text-right p-3 text-sm">Qty</th>
                      <th className="text-right p-3 text-sm">Rate</th>
                      <th className="text-right p-3 text-sm">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewInvoice.items.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-3">{index + 1}</td>
                        <td className="p-3 whitespace-pre-line">{item.description}</td>
                        <td className="p-3 text-right">{item.quantity.toFixed(2)}</td>
                        <td className="p-3 text-right">{item.rate.toFixed(2)}</td>
                        <td className="p-3 text-right">{item.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end mb-8">
                  <div className="w-64">
                    <div className="flex justify-between py-2">
                      <span>Sub Total</span>
                      <span>{previewInvoice.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 font-bold text-lg">
                      <span>Total</span>
                      <span>{formatCurrency(previewInvoice.total)}</span>
                    </div>
                    <div className="flex justify-between py-3 bg-gray-700 text-white px-4 mt-2">
                      <span className="font-bold">Balance Due</span>
                      <span className="font-bold">{formatCurrency(previewInvoice.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Notes & Terms */}
                <div className="space-y-4">
                  {previewInvoice.notes && (
                    <div>
                      <h4 className="font-bold">Notes</h4>
                      <p className="text-gray-600">{previewInvoice.notes}</p>
                    </div>
                  )}
                  {previewInvoice.payment_terms && (
                    <div>
                      <h4 className="font-bold">Terms & Conditions</h4>
                      <p className="text-gray-600">{previewInvoice.payment_terms}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Invoice Dialog */}
        <Dialog open={showEditInvoiceDialog} onOpenChange={setShowEditInvoiceDialog}>
          <DialogContent className="bg-slate-950 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Invoice</DialogTitle>
            </DialogHeader>
            {editingInvoice && (
              <div className="space-y-4 mt-4">
                {/* Invoice Number */}
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Invoice Number</label>
                  <input
                    type="text"
                    value={editingInvoice.invoice_number}
                    onChange={(e) => setEditingInvoice({ ...editingInvoice, invoice_number: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 font-mono"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Invoice Date</label>
                    <input
                      type="date"
                      value={editingInvoice.invoice_date}
                      onChange={(e) => setEditingInvoice({ ...editingInvoice, invoice_date: e.target.value })}
                      className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Due Date</label>
                    <input
                      type="date"
                      value={editingInvoice.due_date}
                      onChange={(e) => setEditingInvoice({ ...editingInvoice, due_date: e.target.value })}
                      className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Terms</label>
                  <input
                    type="text"
                    value={editingInvoice.terms}
                    onChange={(e) => setEditingInvoice({ ...editingInvoice, terms: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Status</label>
                  <select
                    value={editingInvoice.status}
                    onChange={(e) => setEditingInvoice({ ...editingInvoice, status: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Notes</label>
                  <textarea
                    value={editingInvoice.notes || ''}
                    onChange={(e) => setEditingInvoice({ ...editingInvoice, notes: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 h-20"
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Payment Terms</label>
                  <textarea
                    value={editingInvoice.payment_terms || ''}
                    onChange={(e) => setEditingInvoice({ ...editingInvoice, payment_terms: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 h-20"
                  />
                </div>

                <Button onClick={handleSaveInvoice} className="w-full bg-primary">
                  Save Changes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminInvoicesPage;
